import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";

const LiveMarketContext = createContext(null);
const POLL_MS = 4000;

export const LiveMarketProvider = ({ children }) => {
  const [prices, setPrices] = useState({});   // id -> pricing
  const [dirs, setDirs] = useState({});        // id -> 'up' | 'down'
  const [history, setHistory] = useState({});  // id -> [live_competitor, ...] (max 14)
  const prevRef = useRef({});
  const histRef = useRef({});
  const listenersRef = useRef([]); // price-drop subscribers

  const onDrop = useCallback((fn) => {
    listenersRef.current.push(fn);
    return () => {
      listenersRef.current = listenersRef.current.filter((f) => f !== fn);
    };
  }, []);

  useEffect(() => {
    let active = true;
    const tick = async () => {
      try {
        const { items } = await api.livePricing();
        if (!active) return;
        const next = {};
        const nextDirs = {};
        items.forEach((it) => {
          next[it.id] = it;
          const prev = prevRef.current[it.id];
          if (prev && it.live_competitor !== prev.live_competitor) {
            nextDirs[it.id] = it.live_competitor > prev.live_competitor ? "up" : "down";
          }
          if (prev && it.live_competitor < prev.live_competitor) {
            listenersRef.current.forEach((fn) => fn(it, prev));
          }
          const h = histRef.current[it.id] || [];
          histRef.current[it.id] = [...h, it.live_competitor].slice(-14);
        });
        prevRef.current = next;
        setPrices(next);
        setDirs(nextDirs);
        setHistory({ ...histRef.current });
      } catch (err) {
        console.warn("Live pricing poll failed; keeping last known prices", err?.message || err);
      }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, []);

  const value = useMemo(() => ({ prices, dirs, history, onDrop }), [prices, dirs, history, onDrop]);

  return <LiveMarketContext.Provider value={value}>{children}</LiveMarketContext.Provider>;
};

export const useLiveMarket = () =>
  useContext(LiveMarketContext) || { prices: {}, dirs: {}, history: {}, onDrop: () => () => {} };
